import { motion } from "framer-motion";
import { Download, Printer, ExternalLink } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import RevealText from "@/components/RevealText";

const tiers = [
  {
    id: "01",
    title: "Foundational Listings",
    lede: "Non-negotiable. Free. Anchor local SEO signals for a Manitoba-focused business.",
    items: [
      { name: "Google Business Profile", url: "https://www.google.com/business/", note: "Category: Security guard service. Service area: Manitoba." },
      { name: "Bing Places for Business", url: "https://www.bingplaces.com/" },
      { name: "Apple Business Connect", url: "https://businessconnect.apple.com/" },
      { name: "YellowPages Canada", url: "https://www.yellowpages.ca/" },
      { name: "Yelp Business", url: "https://biz.yelp.com/" },
      { name: "Foursquare / Factual", url: "https://business.foursquare.com/" },
      { name: "Cylex Canada", url: "https://www.cylex.ca/" },
      { name: "N49 Canadian Directory", url: "https://www.n49.com/" }
    ]
  },
  {
    id: "02",
    title: "Manitoba Chambers & Business Associations",
    lede: "Highest-authority local backlinks available. Membership fees apply — SEO and BD value is exceptional.",
    items: [
      { name: "Manitoba Chambers of Commerce", url: "https://mbchamber.mb.ca/" },
      { name: "Winnipeg Chamber of Commerce", url: "https://winnipeg-chamber.com/" },
      { name: "Brandon Chamber of Commerce", url: "https://www.brandonchamber.ca/" },
      { name: "Steinbach Chamber of Commerce", url: "https://steinbachchamber.com/" },
      { name: "Thompson Chamber of Commerce", url: "https://www.thompsonchamber.ca/" },
      { name: "Portage la Prairie & District Chamber", url: "https://www.portagelaprairiechamber.com/" },
      { name: "Winkler & District Chamber", url: "https://winklerchamber.com/" },
      { name: "Selkirk & District Chamber", url: "https://www.selkirkchamber.mb.ca/" },
      { name: "Dauphin & District Chamber", url: "https://www.dauphinchamber.ca/" },
      { name: "Flin Flon Chamber of Commerce", url: "https://flinflonchamber.com/" },
      { name: "The Pas Chamber of Commerce", url: "https://thepaschamber.com/" },
      { name: "Morden & District Chamber", url: "https://mordenchamber.com/" }
    ]
  },
  {
    id: "03",
    title: "Manitoba Industry Associations",
    lede: "Directly relevant to security-adjacent verticals. Members and vendors are typically listed with a link back.",
    items: [
      { name: "Winnipeg Construction Association", url: "https://www.wca.mb.ca/" },
      { name: "Manitoba Home Builders' Association", url: "https://homebuilders.mb.ca/" },
      { name: "Manitoba Heavy Construction Association", url: "https://www.mhca.mb.ca/" },
      { name: "Manitoba Trucking Association", url: "https://www.trucking.mb.ca/" },
      { name: "Mining Association of Manitoba (MAMI)", url: "https://mining.mb.ca/" },
      { name: "BOMA Manitoba", url: "https://bomamanitoba.ca/" },
      { name: "Retail Council of Canada — Manitoba", url: "https://www.retailcouncil.org/" },
      { name: "Manitoba Hotel Association", url: "https://www.manitobahotels.ca/" },
      { name: "Manitoba Restaurant & Foodservices Association", url: "https://mrfa.mb.ca/" }
    ]
  },
  {
    id: "04",
    title: "Security-Industry Associations",
    lede: "Membership + speaker slots. Speaking on mining security or executive protection is high-value earned media.",
    items: [
      { name: "Canadian Society for Industrial Security (CSIS)", url: "https://www.csis-scsi.org/" },
      { name: "ASIS International — Winnipeg Chapter", url: "https://www.asisonline.org/" },
      { name: "International Foundation for Protection Officers (IFPO)", url: "https://ifpo.org/" }
    ]
  },
  {
    id: "05",
    title: "Editorial & Guest Post Targets",
    lede: "Pitch original, insight-driven pieces. Never pitch generic 'why security matters' content.",
    items: [
      { name: "Winnipeg Free Press (business section)", url: "https://www.winnipegfreepress.com/" },
      { name: "Brandon Sun", url: "https://www.brandonsun.com/" },
      { name: "Thompson Citizen", url: "https://www.thompsoncitizen.net/" },
      { name: "Manitoba Cooperator", url: "https://www.manitobacooperator.ca/" },
      { name: "Canadian Security Magazine", url: "https://www.canadiansecuritymag.com/" },
      { name: "Northern Miner (mining trade)", url: "https://www.northernminer.com/" },
      { name: "CBC Manitoba (news tips)", url: "https://www.cbc.ca/news/canada/manitoba" }
    ]
  }
];

const prohibited = [
  "Paid backlinks from generic directories ('submit URL for $49').",
  "Comment spam or forum signature links.",
  "Private blog network (PBN) links.",
  "Reciprocal link exchanges at scale.",
  "Foreign-language directories with no Manitoba connection."
];

export default function Playbook() {
  useSEO({
    title:
      "Backlink Outreach Playbook — Manitoba Security Services",
    description:
      "A curated, prioritised list of Manitoba-specific and industry-specific places where Manitoba Security Services should be listed, mentioned, or linked from.",
    keywords: [
      "Manitoba backlink strategy",
      "Manitoba business directories",
      "Manitoba chamber of commerce",
      "security industry associations Manitoba",
      "local SEO Manitoba"
    ],
    canonical:
      typeof window !== "undefined" ? window.location.origin + "/playbook" : "/playbook",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "The Backlink Playbook — Manitoba Security Services",
      description:
        "A working outreach guide for the Manitoba directories, chambers, industry associations, and editorial outlets worth pursuing.",
      author: {
        "@type": "Organization",
        name: "Manitoba Security Services",
      },
      publisher: {
        "@type": "Organization",
        name: "Manitoba Security Services",
      },
      keywords:
        "Manitoba backlink strategy, Manitoba business directories, Manitoba chamber of commerce, local SEO Manitoba",
    },
  });

  return (
    <div className="bg-[#050505] pt-32 md:pt-40">
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 pb-16 md:pb-24 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          <p className="font-editorial italic text-[#FF3B30] text-sm">
            Index — 06
          </p>
        </div>
        <div className="col-span-12 md:col-span-9">
          <RevealText
            lines={["The Backlink", "Playbook."]}
            className="font-display font-black uppercase tracking-tightest leading-[0.88] text-white text-5xl md:text-7xl lg:text-8xl"
            stagger={0.1}
            delay={0.05}
          />
          <p className="mt-8 font-body text-base md:text-lg text-[#a0a0a0] max-w-2xl leading-relaxed">
            A working outreach guide for the Manitoba directories, chambers,
            industry associations, and editorial outlets worth pursuing. Every
            entry is a legitimate target that respects search-engine
            guidelines — no link farms, no paid schemes.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="/backlink-playbook.md"
              download="Manitoba-Security-Backlink-Playbook.md"
              data-testid="playbook-download-md"
              className="group inline-flex items-center gap-3 border border-white/20 hover:border-[#FF3B30] px-6 py-3 font-display uppercase tracking-[0.16em] text-xs md:text-sm text-white transition-colors"
            >
              <Download size={16} />
              Download (Markdown)
            </a>
            <button
              type="button"
              onClick={() => typeof window !== "undefined" && window.print()}
              data-testid="playbook-print"
              className="group inline-flex items-center gap-3 border border-white/20 hover:border-[#FF3B30] px-6 py-3 font-display uppercase tracking-[0.16em] text-xs md:text-sm text-white transition-colors"
            >
              <Printer size={16} />
              Print / Save as PDF
            </button>
          </div>
        </div>
      </section>

      {/* Tiers */}
      {tiers.map((t, ti) => (
        <section
          key={t.id}
          data-testid={`playbook-tier-${t.id}`}
          className="border-t border-[#1e1e1e]"
        >
          <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-16 md:py-24 grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              >
                <p className="font-editorial italic text-[#FF3B30] text-sm">
                  Tier {t.id}
                </p>
                <h2 className="mt-4 font-display font-black uppercase tracking-tight text-white text-3xl md:text-4xl leading-tight">
                  {t.title}
                </h2>
                <p className="mt-4 font-editorial italic text-[#a0a0a0] text-base md:text-lg">
                  {t.lede}
                </p>
              </motion.div>
            </div>
            <div className="col-span-12 md:col-span-8">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                {t.items.map((it, i) => (
                  <motion.li
                    key={it.url}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-30px" }}
                    transition={{
                      duration: 0.6,
                      delay: (i % 8) * 0.03,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="border-b border-[#1e1e1e]"
                  >
                    <a
                      href={it.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      data-testid={`playbook-link-${ti}-${i}`}
                      className="group flex items-baseline justify-between gap-4 py-4 hover:border-[#FF3B30] transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="font-display uppercase tracking-tight text-white text-sm md:text-base group-hover:translate-x-1 transition-transform">
                          {it.name}
                        </p>
                        {it.note && (
                          <p className="mt-1 font-editorial italic text-[#6a6a6a] text-xs md:text-sm">
                            {it.note}
                          </p>
                        )}
                      </div>
                      <ExternalLink
                        size={14}
                        className="text-white/40 group-hover:text-[#FF3B30] flex-shrink-0"
                      />
                    </a>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ))}

      {/* Pitch template */}
      <section className="border-t border-[#1e1e1e] bg-[#0b0b0b]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-28 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-4">
            <p className="font-editorial italic text-[#FF3B30] text-sm">
              Template
            </p>
            <h2 className="mt-4 font-display font-black uppercase tracking-tight text-white text-3xl md:text-4xl leading-tight">
              The Editorial Pitch
            </h2>
            <p className="mt-4 font-editorial italic text-[#a0a0a0] text-base md:text-lg">
              Adapt one paragraph per publication. Never send generic mass
              pitches — every editor sees them, every editor deletes them.
            </p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <pre
              data-testid="pitch-template"
              className="whitespace-pre-wrap font-body text-sm md:text-base text-[#a0a0a0] leading-relaxed border border-[#1e1e1e] p-6 md:p-8 bg-[#050505]"
            >{`Subject: Story idea — [specific Manitoba security angle]

Hi [Editor],

I run Manitoba Security Services and I have a story I think would fit
[publication]\u2019s coverage of [industry]. [One-sentence hook — e.g.
\u201CCopper theft on Winnipeg construction sites has doubled since 2023
and insurance underwriters are quietly changing what they require of
general contractors.\u201D]

I can offer: [data / case study / expert quotes]. Word count target:
[700-1200]. No promotional content — the piece stands as journalism.

Happy to send a draft outline or discuss on the phone.

[Name]`}</pre>
          </div>
        </div>
      </section>

      {/* Prohibited */}
      <section className="border-t border-[#1e1e1e]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-28 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-4">
            <p className="font-editorial italic text-[#FF3B30] text-sm">
              Prohibited
            </p>
            <h2 className="mt-4 font-display font-black uppercase tracking-tight text-white text-3xl md:text-4xl leading-tight">
              What to never do.
            </h2>
            <p className="mt-4 font-editorial italic text-[#a0a0a0] text-base md:text-lg">
              Google penalises every one of the tactics below. Avoid them
              even when a vendor promises &ldquo;guaranteed&rdquo; placements.
            </p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <ul className="space-y-4">
              {prohibited.map((p, i) => (
                <li
                  key={i}
                  data-testid={`prohibited-${i}`}
                  className="flex items-start gap-4 border-b border-[#1e1e1e] pb-4"
                >
                  <span className="font-editorial italic text-[#FF3B30] text-xl">
                    ×
                  </span>
                  <span className="font-body text-base md:text-lg text-[#a0a0a0]">
                    {p}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
